import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Trophy, Users, DollarSign, FileText, Clock, CheckCircle,
  PlayCircle, TrendingUp,
} from "lucide-react";
import AdminCard from "../../components/admin/ui/AdminCard";
import AdminStatCard from "../../components/admin/ui/AdminStatCard";
import ChartOrEmpty from "../../components/admin/ui/ChartOrEmpty";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatVND } from "../../utils/helpers";
import { useThemeStore } from "../../store/themeStore";
import {
  hasCounts, shortMoney, donutOptions, areaTrendOptions, columnTrendOptions, barByStatusOptions,
} from "../../utils/chartHelpers";

const DashboardPage = ({ statsLoader, basePath, title }) => {
  const navigate = useNavigate();
  const isDark = useThemeStore((s) => s.theme === "dark");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    statsLoader()
      .then(setStats)
      .catch((err) => toast.error(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [statsLoader]);

  const cards = stats
    ? [
        {
          icon: Trophy, accent: "indigo", label: "Tổng giải đấu",
          value: stats.tournaments.total,
          hint: `${stats.tournaments.active} đang hoạt động`,
        },
        {
          icon: FileText, accent: "cyan", label: "Đang mở đăng ký",
          value: stats.tournaments.openForRegistration,
          hint: "Giải đấu",
        },
        {
          icon: PlayCircle, accent: "amber", label: "Đang thi đấu",
          value: stats.tournaments.inProgress,
          hint: "Giải đấu",
        },
        {
          icon: CheckCircle, accent: "emerald", label: "Đã hoàn thành",
          value: stats.tournaments.completed,
          hint: "Giải đấu",
        },
        {
          icon: DollarSign, accent: "emerald", label: "Tổng doanh thu",
          value: formatVND(stats.revenue.totalRevenue || 0),
          hint: `TB ${formatVND(stats.revenue.avgTicketValue || 0)} / giao dịch`,
        },
        {
          icon: Users, accent: "indigo", label: "Người tham gia",
          value: stats.participants.total,
          hint: `${stats.participants.active} đang thi đấu`,
        },
        {
          icon: Clock, accent: "amber", label: "Đăng ký chờ xử lý",
          value: stats.registrations.pending,
          hint: `${stats.registrations.total} tổng đăng ký`,
        },
        {
          icon: TrendingUp, accent: "cyan", label: "Tỷ lệ trận đã đấu",
          value: `${Math.round(stats.matches.completionRate)}%`,
          hint: `${stats.matches.completed}/${stats.matches.total} trận`,
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h1>
        <p className="text-sm text-slate-500 dark:text-white/60">Tổng quan hệ thống giải đấu</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="admin-card h-32 animate-pulse bg-slate-100 dark:bg-white/10" />
          ))}
        </div>
      ) : !stats ? (
        <p className="text-sm text-slate-400 dark:text-white/40">Không tải được dữ liệu thống kê.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {cards.map((c) => (
              <AdminStatCard key={c.label} {...c} />
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <AdminCard title="Doanh thu theo tháng" padding={false}>
              <div className="px-2 pb-2">
                <ChartOrEmpty
                  hasData={stats.revenue.monthlyTrend.some((p) => Number(p.amount) > 0)}
                  options={areaTrendOptions(
                    stats.revenue.monthlyTrend,
                    "Doanh thu",
                    "#4f46e5",
                    "amount",
                    shortMoney,
                    isDark
                  )}
                />
              </div>
            </AdminCard>
            <AdminCard title="Giải đấu mới theo tháng" padding={false}>
              <div className="px-2 pb-2">
                <ChartOrEmpty
                  hasData={stats.tournaments.monthlyTrend.some((p) => p.count > 0)}
                  options={columnTrendOptions(stats.tournaments.monthlyTrend, "Giải mới", "#06b6d4", isDark)}
                />
              </div>
            </AdminCard>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <AdminCard title="Giải đấu theo trạng thái" padding={false}>
              <div className="px-2 pb-2">
                <ChartOrEmpty
                  hasData={hasCounts(stats.tournaments.byStatus)}
                  options={donutOptions(stats.tournaments.byStatus, "Giải đấu", isDark)}
                />
              </div>
            </AdminCard>
            <AdminCard title="Đăng ký theo trạng thái" padding={false}>
              <div className="px-2 pb-2">
                <ChartOrEmpty
                  hasData={hasCounts(stats.registrations.byStatus)}
                  options={donutOptions(stats.registrations.byStatus, "Đăng ký", isDark)}
                />
              </div>
            </AdminCard>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <AdminCard title="Trận đấu theo trạng thái" padding={false}>
              <div className="px-2 pb-2">
                <ChartOrEmpty
                  hasData={hasCounts(stats.matches.byStatus)}
                  options={barByStatusOptions(stats.matches.byStatus, "Trận đấu", isDark)}
                />
              </div>
            </AdminCard>
            <AdminCard title="Loại bi phổ biến" padding={false}>
              <div className="px-2 pb-2">
                <ChartOrEmpty
                  hasData={hasCounts(stats.tournaments.byGameType)}
                  options={barByStatusOptions(stats.tournaments.byGameType, "Giải đấu", isDark)}
                />
              </div>
            </AdminCard>
          </div>
        </>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => navigate(`${basePath}/tournaments`)}
          className="flex items-center gap-3 bg-white dark:bg-[#161a22] rounded-2xl p-5 shadow-sm border border-slate-100 dark:border-white/10 text-left hover:border-[#010851] transition-colors group"
        >
          <Trophy size={20} className="text-[#010851] shrink-0" />
          <div>
            <p className="font-semibold text-[#010851] group-hover:text-[#EF342A] transition-colors">Quản lý giải đấu</p>
            <p className="text-xs text-slate-400 dark:text-white/40">Tạo, cấu hình, mở đăng ký</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => navigate(`${basePath}/employees`)}
          className="flex items-center gap-3 bg-white dark:bg-[#161a22] rounded-2xl p-5 shadow-sm border border-slate-100 dark:border-white/10 text-left hover:border-[#010851] transition-colors group"
        >
          <Users size={20} className="text-[#010851] shrink-0" />
          <div>
            <p className="font-semibold text-[#010851] group-hover:text-[#EF342A] transition-colors">Quản lý nhân viên</p>
            <p className="text-xs text-slate-400 dark:text-white/40">Manager, Staff</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => navigate(`${basePath}/news`)}
          className="flex items-center gap-3 bg-white dark:bg-[#161a22] rounded-2xl p-5 shadow-sm border border-slate-100 dark:border-white/10 text-left hover:border-[#010851] transition-colors group"
        >
          <FileText size={20} className="text-[#010851] shrink-0" />
          <div>
            <p className="font-semibold text-[#010851] group-hover:text-[#EF342A] transition-colors">Tin tức & Bài viết</p>
            <p className="text-xs text-slate-400 dark:text-white/40">CMS — tạo, xuất bản tin</p>
          </div>
        </button>
      </div>
    </div>
  );
};

export default DashboardPage;
